
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';

export default function Home() {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const fullText = "Empowering Tomorrow's Leaders";
  const typingSpeed = 100; // milliseconds per character
  const pauseDuration = 2000; // pause at end before restarting

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (!isDeleting && currentIndex < fullText.length) {
      // Typing forward
      timeout = setTimeout(() => {
        setDisplayedText(fullText.substring(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, typingSpeed);
    } else if (!isDeleting && currentIndex === fullText.length) {
      // Pause at end
      timeout = setTimeout(() => {
        setIsDeleting(true);
      }, pauseDuration);
    } else if (isDeleting && currentIndex > 0) {
      // Deleting backward
      timeout = setTimeout(() => {
        setDisplayedText(fullText.substring(0, currentIndex - 1));
        setCurrentIndex(currentIndex - 1);
      }, typingSpeed / 2);
    } else if (isDeleting && currentIndex === 0) {
      // Reset and start over
      setIsDeleting(false);
    }

    return () => clearTimeout(timeout);
  }, [currentIndex, isDeleting]);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat overflow-hidden pt-20" 
     style={{backgroundImage: `url('https://static.readdy.ai/image/e999ec2dea4e4b618de520757ef9db5d/a7c3ff689189f361137f348d1ab7570c.png')`}}>
   {/* Minimal overlay for text readability */}
   <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative z-10 w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="text-white space-y-8">
                <div className="space-y-6">
                  <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                    <span className="text-[#C6A44C] text-sm font-medium">🎓 Excellence in Education Since 2019</span>
                  </div>
                  <h1 className="text-5xl md:text-7xl font-bold leading-tight min-h-[200px] md:min-h-[300px]">
                    <span className="inline-block">
                      {displayedText}
                      <span className="inline-block w-1 h-16 md:h-24 bg-[#C6A44C] ml-2 animate-pulse" style={{ verticalAlign: 'middle' }}></span>
                    </span>
                  </h1>
                  <p className="text-xl md:text-2xl opacity-90 leading-relaxed max-w-2xl">
                    Join a community of innovators, thinktanks, and leaders. Experience world-class education that transforms potential into excellence.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/admissions" className="group relative overflow-hidden bg-[#C6A44C] hover:bg-[#B8954A] text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                    <span className="relative z-10">Start Your Journey</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-[#D4B55C] to-[#C6A44C] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </Link>
                  <Link to="/about" className="group border-2 border-white/30 hover:border-white text-white hover:bg-white hover:text-[#005A2C] px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm">
                    Discover More
                  </Link>
                </div>
                <div className="flex items-center space-x-8 pt-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#C6A44C]">300+</div>
                    <div className="text-sm opacity-80">Students</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#C6A44C]">10+</div>
                    <div className="text-sm opacity-80">Faculty</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#C6A44C]">98+</div>
                    <div className="text-sm opacity-80">Success Rate</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23f0f0f0%22%20fill-opacity%3D%220.6%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-80"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-[#388E3C]/10 rounded-full mb-6">
              <span className="text-[#2E7D32] text-sm font-semibold">College Life</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2E7D32] mb-6">Excellence in Every Aspect</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Discover what makes us the preferred choice for quality education and holistic development</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-br from-[#388E3C] to-[#2E7D32] rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-graduation-cap-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#2E7D32] mb-4 text-center">Academic Excellence</h3>
              <p className="text-gray-600 text-center leading-relaxed">Rigorous curriculum designed to challenge and inspire students to reach their full potential with innovative teaching methods.</p>
            </div>

            <div className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-br from-[#C6A44C] to-[#B8954A] rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-team-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#2E7D32] mb-4 text-center">Expert Faculty</h3>
              <p className="text-gray-600 text-center leading-relaxed">Dedicated teachers and professors committed to nurturing each student's growth and success with personalized attention.</p>
            </div>

            <div className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-br from-[#388E3C] to-[#2E7D32] rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-lightbulb-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#2E7D32] mb-4 text-center">Innovation Focus</h3>
              <p className="text-gray-600 text-center leading-relaxed">Cutting-edge facilities and modern teaching methods to prepare students for the challenges of tomorrow.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#2E7D32] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#2E7D32] via-[#388E3C] to-[#2E7D32]"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-[#C6A44C] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-[#C6A44C] rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Our Impact in Numbers</h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">Decades of excellence reflected in our achievements and student success stories</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 group-hover:bg-white/20 transition-all duration-300">
                <div className="text-5xl md:text-6xl font-bold text-[#C6A44C] mb-2">300+</div>
                <div className="text-white/80 text-lg">Active Students</div>
              </div>
            </div>
            <div className="text-center group">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 group-hover:bg-white/20 transition-all duration-300">
                <div className="text-5xl md:text-6xl font-bold text-[#C6A44C] mb-2">10+</div>
                <div className="text-white/80 text-lg">Expert Faculty</div>
              </div>
            </div>
            <div className="text-center group">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 group-hover:bg-white/20 transition-all duration-300">
                <div className="text-5xl md:text-6xl font-bold text-[#C6A44C] mb-2">15+</div>
                <div className="text-white/80 text-lg">Programs</div>
              </div>
            </div>
            <div className="text-center group">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 group-hover:bg-white/20 transition-all duration-300">
                <div className="text-5xl md:text-6xl font-bold text-[#C6A44C] mb-2">98+</div>
                <div className="text-white/80 text-lg">Success Rate</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest News & Events */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-[#388E3C]/10 rounded-full mb-6">
              <span className="text-[#2E7D32] text-sm font-semibold">College Life</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2E7D32] mb-6">Latest News &amp; Events</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Stay updated with the exciting happenings and achievements at Elite College</p>
          </div>
          
          <div className="flex justify-center">
            <div className="max-w-md">
              <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100">
                <div className="relative overflow-hidden">
                  <img src="https://static.readdy.ai/image/e999ec2dea4e4b618de520757ef9db5d/161832b976c33d6e8b2bafa028442993.jpeg" 
                       alt="Great Zimbabwe Trip" className="w-full h-48 object-cover object-top group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute top-4 left-4 bg-[#C6A44C] text-white px-3 py-1 rounded-full text-sm font-semibold">
                    Latest
                  </div>
                </div>
                <div className="p-6">
                  <div className="text-sm text-[#388E3C] mb-2 font-medium">03 October 2025</div>
                  <h3 className="text-xl font-bold text-[#2E7D32] mb-3 group-hover:text-[#388E3C] transition-colors">Educational Trip to Great Zimbabwe</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">Our students embarked on an incredible educational journey to the historic Great Zimbabwe ruins, exploring our rich cultural heritage.</p>
                  <Link to="/news?id=great-zimbabwe-trip" className="inline-flex items-center text-[#388E3C] hover:text-[#C6A44C] font-semibold cursor-pointer transition-colors group">
                    Read More 
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Student Reflections */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23f0f0f0%22%20fill-opacity%3D%220.4%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-60"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-[#C6A44C]/10 rounded-full mb-6">
              <span className="text-[#C6A44C] text-sm font-semibold">💬 Voices of Excellence</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2E7D32] mb-6">Student Reflections</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Hear from our students about their experiences and memorable moments at Elite College</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Reflection 1 */}
            <div className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border-l-4 border-[#C6A44C] relative overflow-hidden">
              <div className="absolute top-4 right-4 text-[#C6A44C]/20 text-6xl leading-none">
                <i className="ri-double-quotes-r"></i>
              </div>
              <div className="relative z-10">
                <p className="text-lg text-gray-700 italic leading-relaxed mb-6">
                  "Walking through Great Zimbabwe was like stepping back in time. It made me proud of our heritage and inspired me to learn more about African history."
                </p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-semibold text-[#2E7D32] text-base">Precious Lonjela</p>
                  <p className="text-sm text-gray-500">Form 1 Student</p>
                </div>
              </div>
            </div>

            {/* Reflection 2 */}
            <div className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border-l-4 border-[#C6A44C] relative overflow-hidden">
              <div className="absolute top-4 right-4 text-[#C6A44C]/20 text-6xl leading-none">
                <i className="ri-double-quotes-r"></i>
              </div>
              <div className="relative z-10">
                <p className="text-lg text-gray-700 italic leading-relaxed mb-6">
                  "The teachers at Elite College don't just teach—they inspire. They've helped me discover my passion for science and believe in my potential."
                </p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-semibold text-[#2E7D32] text-base">Tofara Zhou</p>
                  <p className="text-sm text-gray-500">Form 2 Student</p>
                </div>
              </div>
            </div>

            {/* Reflection 3 */}
            <div className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border-l-4 border-[#C6A44C] relative overflow-hidden">
              <div className="absolute top-4 right-4 text-[#C6A44C]/20 text-6xl leading-none">
                <i className="ri-double-quotes-r"></i>
              </div>
              <div className="relative z-10">
                <p className="text-lg text-gray-700 italic leading-relaxed mb-6">
                  "Being part of the debate club has transformed my confidence. Elite College provides so many opportunities to grow beyond academics."
                </p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-semibold text-[#2E7D32] text-base">Ivy Mashiri</p>
                  <p className="text-sm text-gray-500">Form 3 Student</p>
                </div>
              </div>
            </div>
          </div>

          {/* View More Button */}
          <div className="text-center mt-12">
            <Link to="/gallery" className="inline-flex items-center px-8 py-4 bg-[#C6A44C] hover:bg-[#B8954A] text-white rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
              <span>View More Stories</span>
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-[#2E7D32] via-[#388E3C] to-[#2E7D32] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml,%3Csvg%20width%3D%22100%22%20height%3D%22100%22%20viewBox%3D%220%200%20100%20100%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cpath%20d%3D%22M11%2018c3.866%200%207-3.134%207-7s-3.134-7-7-7-7%203.134-7%207%203.134%207%207%207zm48%2025c3.866%200%207-3.134%207-7s-3.134-7-7-7-7%203.134-7%207%203.134%207%207zm-43-7c1.657%200%203-1.343%203-3s-1.343-3-3-3-3%201.343-3%203%201.343%203%203%203zm63%2031c1.657%200%203-1.343%203-3s-1.343-3-3-3-3%201.343-3%203%201.343%203%203zM34%2090c1.657%200%203-1.343%203-3s-1.343-3-3-3-3%201.343-3%203%201.343%203%203zm56-76c1.657%200%203-1.343%203-3s-1.343-3-3-3-3%201.343-3%203%201.343%203%203zM12%2086c2.21%200%204-1.79%204-4s-1.79-4-4-4-4%201.79-4%204%201.79%204%204%204zm28-65c2.21%200%204-1.79%204-4s-1.79-4-4-4-4%201.79-4%204%201.79%204%204%204zm23-11c2.76%200%205-2.24%205-5s-2.24-5-5-5-5%202.24-5%205%202.24%205%205%205zm-6%2060c2.21%200%204-1.79%204-4s-1.79-4-4-4-4%201.79-4%204%201.79%204%204%204zm29%2022c2.76%200%205-2.24%205-5s-2.24-5-5-5-5%202.24-5%205%202.24%205%205%205zM32%2063c2.76%200%205-2.24%205-5s-2.24-5-5-5-5%202.24-5%205%202.24%205%205%205zm57-13c2.76%200%205-2.24%205-5s-2.24-5-5-5-5%202.24-5%205%202.24%205%205%205zm-9-21c1.105%200%202-.895%202-2s-.895-2-2-2-2%20.895-2%202%20.895%202%202%202zm60%2091c1.105%200%202-.895%202-2s-.895-2-2-2-2%20.895-2%202%20.895%202%202%202zm35%2041c1.105%200%202-.895%202-2s-.895-2-2-2-2%20.895-2%202%20.895%202%202%202zm12%2060c1.105%200%202-.895%202-2s-.895-2-2-2-2%20.895-2%202%20.895%202%202%202z%22%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.2%22%20fill-rule%3D%22evenodd%22/%3E%3C/svg%3E')]"></div>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-12 border border-white/20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Begin Your Journey?</h2>
            <p className="text-xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed">
              Join thousands of successful graduates who started their journey at Elite College. Your future begins with a single step.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/admissions" className="group relative overflow-hidden bg-[#C6A44C] hover:bg-[#B8954A] text-white px-10 py-5 rounded-xl font-bold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer shadow-2xl hover:shadow-3xl transform hover:-translate-y-1">
                <span className="relative z-10 flex items-center justify-center">
                  Start Your Application
                  <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                </span>
              </Link>
              <Link to="/contact" className="group border-2 border-white/50 hover:border-white text-white hover:bg-white hover:text-[#2E7D32] px-10 py-5 rounded-xl font-bold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm">
                <span className="flex items-center justify-center">
                  Schedule a Visit
                  <i className="ri-calendar-line ml-2"></i>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2E7D32] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center mb-6">
                <img 
                  src="https://static.readdy.ai/image/e999ec2dea4e4b618de520757ef9db5d/995999b745e34f0ed4248820e32af9bb.jpeg" 
                  alt="Elite College Logo" 
                  className="h-12 w-12 object-cover rounded-full" />
              </div>
              <p className="text-gray-300 mb-6 text-lg leading-relaxed max-w-md">
                Empowering tomorrow's leaders through excellence in education and holistic development since 2019.
              </p>
              <div className="flex space-x-4">
                <button className="w-12 h-12 bg-[#C6A44C] hover:bg-white hover:text-[#005A2C] rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer group">
                  <i className="ri-facebook-fill text-xl group-hover:scale-110 transition-transform"></i>
                </button>
                <button className="w-12 h-12 bg-[#C6A44C] hover:bg-white hover:text-[#005A2C] rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer group">
                  <i className="ri-twitter-fill text-xl group-hover:scale-110 transition-transform"></i>
                </button>
                <button className="w-12 h-12 bg-[#C6A44C] hover:bg-white hover:text-[#005A2C] rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer group">
                  <i className="ri-instagram-line text-xl group-hover:scale-110 transition-transform"></i>
                </button>
                <button className="w-12 h-12 bg-[#C6A44C] hover:bg-white hover:text-[#005A2C] rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer group">
                  <i className="ri-linkedin-fill text-xl group-hover:scale-110 transition-transform"></i>
                </button>
                <a href="https://wa.me/263774872973" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#C6A44C] hover:bg-white hover:text-[#005A2C] rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer group">
                  <i className="ri-whatsapp-line text-xl group-hover:scale-110 transition-transform"></i>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-xl font-bold mb-6 text-[#C6A44C]">Quick Links</h4>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-gray-300 hover:text-[#C6A44C] cursor-pointer transition-colors text-lg">About Us</Link></li>
                <li><Link to="/admissions" className="text-gray-300 hover:text-[#C6A44C] cursor-pointer transition-colors text-lg">Admissions</Link></li>
                <li><Link to="/gallery" className="text-gray-300 hover:text-[#C6A44C] cursor-pointer transition-colors text-lg">Gallery</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-[#C6A44C] cursor-pointer transition-colors text-lg">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-xl font-bold mb-6 text-[#C6A44C]">Contact Info</h4>
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-start">
                  <i className="ri-map-pin-line mr-3 text-[#C6A44C] mt-1 text-lg"></i>
                  <span className="text-lg">535 Mataga</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-phone-line mr-3 text-[#C6A44C] text-lg"></i>
                  <span className="text-lg">+263 774 872 973</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-mail-line mr-3 text-[#C6A44C] text-lg"></i>
                  <span className="text-lg">info@elitecollege.co.zw</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-[#388E3C] pt-8 text-center text-gray-300">
            <p className="text-lg">&copy; 2026 Elite College. All rights reserved.</p>
            <p className="mt-2">
              Powered by <a href="https://onhightech.co.zw" target="_blank" rel="noopener noreferrer" className="text-[#C6A44C] hover:text-white underline cursor-pointer transition-colors">Onhightech Solutions</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
